import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot, setDoc, updateDoc, arrayUnion, arrayRemove, getDoc, runTransaction } from 'firebase/firestore';
import {
    GRID_W,
    GRID_H,
    MAX_PLAYERS,
    INITIAL_CELLS,
    COUNTRY_DATA
} from '../constants/gameConstants';
import { useGameWinCondition } from './useGameWinCondition';

const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[i], newArray[j]];
    }
    return newArray;
};

const generateRoomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ01234456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
}

const getAvailableColors = () => {
    return Object.values(COUNTRY_DATA).map(country => country.color);
};

const updateHostIfNeeded = async (roomRef, currentPlayers, currentHostId) => {
    try {
        const isOldHostInRoom = currentPlayers.some(p => p.id === currentHostId);
        if (isOldHostInRoom) return;

        const newHost = currentPlayers.find(p => !p.isBot) || currentPlayers[0];
        if (newHost) {
            await updateDoc(roomRef, {
                hostId: newHost.id
            });
        }
    } catch (error) {
        console.warn('Host güncelleme hatası:', error);
    }
};

export const useGameLogic = (roomCode, currentUser) => {
    const [gameState, setGameState] = useState(null);
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [roomData, setRoomData] = useState(null);

    const prevRoomCodeRef = useRef(null);
    const unsubscribeRef = useRef(null);
    const mountedRef = useRef(true);
    
    // ✅ PERFORMANS İYİLEŞTİRMESİ: Deep comparison yerine hash kullan
    const prevDataHashRef = useRef('');

    const createInitialGrid = useCallback((playersArray) => {
        const totalCells = GRID_W * GRID_H;
        const grid = new Array(totalCells).fill(-1);

        const allIndices = Array.from({ length: totalCells }, (_, i) => i);
        const shuffledIndices = shuffleArray(allIndices);

        const cellsPerPlayer = Math.min(INITIAL_CELLS, Math.floor(totalCells / playersArray.length));

        playersArray.forEach((player, playerIndex) => {
            for (let i = 0; i < cellsPerPlayer; i++) {
                const cellIndex = shuffledIndices.pop();
                if (cellIndex !== undefined) {
                    grid[cellIndex] = playerIndex;
                } else {
                    break;
                }
            }
        });

        return grid;
    }, []);

    const getInitialGameState = useCallback(() => ({
        grid: new Array(GRID_H * GRID_W).fill(-1),
        gridW: GRID_W,
        gridH: GRID_H,
        turn: 0,
        turnLimit: 25,
        winner: null,
        currentPlayerIndex: 0,
        hasExpanded: false,
        attackCount: 0,
        playerStats: {},
        log: ['Oyun kurulum aşamasında...']
    }), []);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
            }
        };
    }, []);

    useEffect(() => {
        if (roomCode === prevRoomCodeRef.current) {
            return;
        }

        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }

        if (!roomCode) {
            setLoading(false);
            setGameState(null);
            setPlayers([]);
            setRoomData(null);
            return;
        }

        setLoading(true);
        prevRoomCodeRef.current = roomCode;

        const roomRef = doc(db, 'rooms', roomCode);

        unsubscribeRef.current = onSnapshot(
            roomRef,
            { includeMetadataChanges: false },
            (docSnapshot) => {
                if (!mountedRef.current) return;

                if (docSnapshot.exists()) {
                    const data = docSnapshot.data();

                    // ✅ PERFORMANS: Sadece kritik alanları hash'le
                    const currentHash = JSON.stringify({
                        grid: data.gameState?.grid,
                        turn: data.gameState?.turn,
                        currentPlayerIndex: data.gameState?.currentPlayerIndex,
                        winner: data.gameState?.winner,
                        attackCount: data.gameState?.attackCount,
                        hasExpanded: data.gameState?.hasExpanded,
                        playersCount: data.players?.length,
                        status: data.status
                    });

                    // Gerçekten değişiklik varsa güncelle
                    if (currentHash !== prevDataHashRef.current) {
                        prevDataHashRef.current = currentHash;
                        setRoomData(data);
                        setGameState(data.gameState);
                        setPlayers(data.players || []);
                    }
                } else {
                    setGameState(null);
                    setPlayers([]);
                    setRoomData(null);
                    prevDataHashRef.current = '';
                }

                setLoading(false);
            },
            (error) => {
                if (!mountedRef.current) return;
                console.error('Firestore dinleyici hatası:', error);
                setLoading(false);
            }
        );

        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, [roomCode]);

    const myPlayerIndex = useMemo(() => {
        if (!currentUser || !players.length) return -1;
        return players.findIndex(p => p.id === currentUser.uid);
    }, [currentUser, players]);

    const { 
        territoryCounts, 
        checkAndFinalizeWin, 
        winnerId: winConditionWinnerId
    } = useGameWinCondition(
        roomCode,
        gameState,
        players,
        myPlayerIndex
    );

    const activePlayersCount = useMemo(() => {
        if (!territoryCounts || territoryCounts.length === 0) return 0;
        
        return territoryCounts.filter(count => count > 0).length;
    }, [territoryCounts]);

    const isGameActive = useMemo(() => {
        return gameState && gameState.turn > 0 && !gameState.winner;
    }, [gameState]);

    const createRoom = useCallback(async (playerName) => {
        try {
            if (!currentUser) {
                throw new Error('Kullanıcı girişi bulunamadı.');
            }

            const cleanPlayerName = playerName.trim();
            if (!cleanPlayerName) {
                throw new Error('Oyuncu adı boş olamaz!');
            }

            const newRoomCode = generateRoomCode();
            const roomRef = doc(db, 'rooms', newRoomCode);

            const availableColors = getAvailableColors();
            const playerColor = availableColors[0] || '#808080';

            const initialGameState = getInitialGameState();

            const roomData = {
                code: newRoomCode,
                players: [{
                    name: cleanPlayerName,
                    id: currentUser.uid,
                    email: currentUser.email || '',
                    isHost: true,
                    isBot: false,
                    country: null,
                    color: playerColor,
                    attackPower: 0,
                    defensePower: 0,
                }],
                hostId: currentUser.uid,
                status: 'waiting',
                gameState: initialGameState,
                createdAt: new Date(),
                lastUpdated: new Date()
            };

            await setDoc(roomRef, roomData);

            return newRoomCode;
        } catch (error) {
            console.error('Oda oluşturma hatası:', error);
            throw error;
        }
    }, [currentUser, getInitialGameState]);

    const joinRoom = useCallback(async (roomCode, playerName) => {
        try {
            if (!currentUser) {
                throw new Error('Kullanıcı girişi bulunamadı. Lütfen tekrar giriş yapın.');
            }

            if (!roomCode || !playerName.trim()) {
                throw new Error('Oda kodu ve oyuncu adı boş olamaz!');
            }

            const cleanRoomCode = roomCode.trim().toUpperCase();
            const cleanPlayerName = playerName.trim();
            const roomRef = doc(db, 'rooms', cleanRoomCode);

            const result = await runTransaction(db, async (transaction) => {
                const roomDoc = await transaction.get(roomRef);

                if (!roomDoc.exists()) {
                    throw new Error(`"${cleanRoomCode}" kodlu oda bulunamadı!`);
                }

                const roomData = roomDoc.data();
                const currentPlayers = roomData.players || [];

                const existingPlayerIndex = currentPlayers.findIndex(p => p.id === currentUser.uid);
                if (existingPlayerIndex !== -1) {
                    if (currentPlayers[existingPlayerIndex].name !== cleanPlayerName) {
                        const updatedPlayers = [...currentPlayers];
                        updatedPlayers[existingPlayerIndex] = {
                            ...updatedPlayers[existingPlayerIndex],
                            name: cleanPlayerName
                        };
                        transaction.update(roomRef, { players: updatedPlayers });
                    }
                    return { success: true, isRejoin: true };
                }

                if (currentPlayers.length >= MAX_PLAYERS) {
                    throw new Error(`Oda dolu! Maksimum ${MAX_PLAYERS} oyuncu olabilir.`);
                }

                if (roomData.status !== 'waiting') {
                    throw new Error('Oyun başlamış! Yeni oyuncu kabul edilmiyor.');
                }

                const usedColors = currentPlayers.map(p => p.color);
                const availableColors = getAvailableColors().filter(color => !usedColors.includes(color));
                const playerColor = availableColors[0] || '#808080';

                const newPlayer = {
                    id: currentUser.uid,
                    name: cleanPlayerName,
                    isHost: false,
                    isBot: false,
                    country: null,
                    color: playerColor,
                    attackPower: 0,
                    defensePower: 0,
                };

                transaction.update(roomRef, {
                    players: arrayUnion(newPlayer),
                    lastUpdated: new Date()
                });

                return { success: true, isRejoin: false };
            });

            return result;

        } catch (error) {
            console.error('Odaya katılma hatası:', error);
            throw error;
        }
    }, [currentUser]);

    const leaveGame = useCallback(async (roomCode, playerId) => {
        try {
            if (!roomCode || !playerId) return;

            const cleanRoomCode = roomCode.trim().toUpperCase();
            const roomRef = doc(db, 'rooms', cleanRoomCode);
            const roomDoc = await getDoc(roomRef);

            if (!roomDoc.exists()) return;

            const roomData = roomDoc.data();
            const playerToRemove = roomData.players.find(p => p.id === playerId);

            if (!playerToRemove) return;

            await updateDoc(roomRef, {
                players: arrayRemove(playerToRemove),
                lastUpdated: new Date()
            });

            const remainingPlayers = roomData.players.filter(p => p.id !== playerId);
            updateHostIfNeeded(roomRef, remainingPlayers, roomData.hostId).catch(() => {
            });

        } catch (error) {
            console.error('Oyundan ayrılma hatası:', error);
        }
    }, []);

    const endTurn = useCallback(async () => {
        if (!roomCode || !gameState || !players.length || gameState.winner) return;
        
        const roomRef = doc(db, 'rooms', roomCode);
        
        if (myPlayerIndex !== gameState.currentPlayerIndex) {
             console.warn("Sıranız değilken tur bitirmeye çalıştınız!");
             return;
        }
        
        const nextPlayerIndex = (gameState.currentPlayerIndex + 1) % players.length;
        const newTurn = nextPlayerIndex === 0 ? gameState.turn + 1 : gameState.turn;
        
        try {
            await updateDoc(roomRef, {
                'gameState.turn': newTurn,
                'gameState.currentPlayerIndex': nextPlayerIndex,
                'gameState.hasExpanded': false,
                'gameState.attackCount': 0,
                'gameState.log': [
                    ...(gameState.log || []),
                    `${players[gameState.currentPlayerIndex].name} turu bitirdi. Sıra: ${players[nextPlayerIndex].name}`
                ],
                lastUpdated: new Date()
            });
            
            checkAndFinalizeWin(); 
            
        } catch (error) {
            console.error("Tur bitirme hatası:", error);
        }
    }, [roomCode, gameState, players, myPlayerIndex, checkAndFinalizeWin]);

    
    return {
        gameState,
        players,
        loading,
        roomData,

        activePlayersCount,
        isGameActive,
        myPlayerIndex,
        
        territoryCounts,
        checkAndFinalizeWin, 
        gameWinnerId: gameState?.winner || winConditionWinnerId, 

        createRoom,
        joinRoom,
        leaveGame,
        endTurn,
    };
};